import { useState } from "react";
import { POODLE_CHARACTERS, LOGO_IMAGE, VIDEO_BACKGROUND } from "./constants";
import ParentsCorner from "./ParentsCorner";
import PoodleTalkSignInPill from "./poodletalk/PoodleTalkSignInPill";
import OrgSignInPill from "./poodletalk/OrgSignInPill";

const CharacterSelection = ({ onSelect, ageMode, setAgeMode, onNavigate }) => {
  const [showParentsCorner, setShowParentsCorner] = useState(false);

  return (
    <div className="min-h-screen flex flex-col relative">
      <video 
        autoPlay loop muted playsInline
        className="fixed inset-0 w-full h-full object-cover z-0"
      >
        <source src={VIDEO_BACKGROUND} type="video/mp4" />
      </video>
      
      <header className="relative z-20 p-4 sm:p-6 flex justify-center lg:justify-start">
        <a 
          href="https://www.poodlesandfriends.uk/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-block group relative"
          title="Visit our website"
        >
          <img 
            src={LOGO_IMAGE} 
            alt="Poodles and Friends" 
            className="h-72 sm:h-64 lg:h-56 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap bg-white/95 text-[#4A4A4A] px-4 py-2 rounded-full text-sm font-medium shadow-lg border border-[#E0E0E0]">
            Visit our website? 🐩
          </div>
        </a>
        {/* Sign-in pills — personal Google (left) and Organisation (right of it) */}
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-30 flex items-center gap-2">
          <OrgSignInPill onOpen={() => onNavigate && onNavigate('organisations')} />
          <PoodleTalkSignInPill />
        </div>
      </header>
      
      <main className="flex-1 flex flex-col items-center justify-center p-3 sm:p-6 relative z-20">
        <div className="max-w-2xl lg:max-w-5xl w-full">
          {/* EU AI Act transparency notice — plain text above the headline,
              matching the h2 style so it looks like part of the page, no card. */}
          <div className="text-center mb-3 sm:mb-4" data-testid="ai-transparency-panel">
            <p className="text-sm sm:text-base font-semibold text-white drop-shadow-lg" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
              Poodle Talk — A Safe AI Companion for Children
            </p>
            <p className="text-xs sm:text-sm text-white/90 mt-1 drop-shadow-lg" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
              Each poodle friend is powered by AI, not a real person. Please use with a parent, carer or trusted adult.
            </p>
          </div>

          <h2 className="text-xl sm:text-3xl font-bold text-center text-white mb-3 sm:mb-4 drop-shadow-lg" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
            Who would you like to talk to? 🐩
          </h2>

          <div className="flex justify-center mb-3">
            <div className="bg-white/80 backdrop-blur-md rounded-full p-1 flex gap-1 shadow-md">
              <button
                onClick={() => setAgeMode('tiny')}
                data-testid="age-mode-tiny"
                className={`px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  ageMode === 'tiny'
                    ? 'bg-[#FCEAEA] text-[#A55B64] shadow-sm'
                    : 'text-[#6B6B6B] hover:text-[#4A4A4A]'
                }`}
              >
                I'm tiny (3–5)
              </button>
              <button
                onClick={() => setAgeMode('kids')}
                data-testid="age-mode-kids"
                className={`px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  ageMode === 'kids'
                    ? 'bg-[#FFF7D6] text-[#B89823] shadow-sm'
                    : 'text-[#6B6B6B] hover:text-[#4A4A4A]'
                }`}
              >
                I'm 5–10
              </button>
              <button
                onClick={() => setAgeMode('teen')}
                data-testid="age-mode-teen"
                className={`px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  ageMode === 'teen'
                    ? 'bg-[#E6F0F9] text-[#4A7AA5] shadow-sm'
                    : 'text-[#6B6B6B] hover:text-[#4A4A4A]'
                }`}
              >
                I'm a teenager
              </button>
            </div>
          </div>

          <p className="text-center text-[11px] sm:text-xs text-white/85 mb-4 drop-shadow font-medium tracking-wide" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.3)' }}>
            ✨ Made for ages 3–10 — and lovely for teens too 💛
          </p>

          <p className="text-center text-xs sm:text-sm text-white/90 mb-4 sm:mb-6 drop-shadow" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.3)' }}>
            Pick a poodle friend to chat with 💛
          </p>
          
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-6">
            {POODLE_CHARACTERS.map((poodle) => (
              <button
                key={poodle.id}
                onClick={() => onSelect(poodle)}
                data-testid={`character-select-${poodle.id}`}
                className="bg-white/90 backdrop-blur-md rounded-2xl sm:rounded-3xl p-3 sm:p-6 shadow-lg border-2 border-white hover:border-[#FCEB9C] transition-all duration-300 hover:scale-105 hover:shadow-xl group"
              >
                <div className="w-20 h-20 sm:w-28 sm:h-28 lg:w-36 lg:h-36 mx-auto mb-2 sm:mb-4 rounded-full overflow-hidden border-2 sm:border-4 border-[#FFF7D6] shadow-md group-hover:border-[#FCEB9C] transition-all">
                  <img 
                    src={poodle.image} 
                    alt={poodle.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <h3 className="text-base sm:text-xl lg:text-2xl font-bold text-[#4A4A4A] mb-1 sm:mb-2">{poodle.name}</h3>
                <p className="text-[11px] sm:text-sm lg:text-base text-[#6B6B6B] leading-tight">{poodle.description}</p>
              </button>
            ))}
          </div>
        </div>
      </main>
      
      <footer className="relative z-20 py-3 px-4">
        <div className="flex flex-col items-center gap-2">
          <div className="text-center text-xs text-white">
            <a 
              href="https://www.poodlesandfriends.uk/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-[#6B6B6B] transition-colors"
            >
              © 2026 Poodle Talk. All rights reserved. | Poodles & Friends is a family-friendly educational music and media project. | Made for ages 3–10 (and lovely for teens too)
            </a>
            <span className="mx-2">·</span>
            <button 
              onClick={() => setShowParentsCorner(true)}
              className="hover:text-[#6B6B6B] transition-colors"
            >
              Parents Corner
            </button>
            <span className="mx-2">·</span>
            <a 
              href="https://www.poodlesandfriends.uk/policy-info" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-[#6B6B6B] transition-colors"
            >
              Full Policy
            </a>
          </div>
          <div className="text-center text-[10px] text-white/70 mt-1.5 px-2 max-w-2xl mx-auto">
            Registered with the Information Commissioner&apos;s Office (ICO). We process personal data in accordance with UK data protection legislation.
          </div>
        </div>
      </footer>

      <ParentsCorner 
        isOpen={showParentsCorner} 
        onClose={() => setShowParentsCorner(false)} 
        onOpenUsageReport={() => onNavigate && onNavigate('hotel-usage-report')}
      />
    </div>
  );
};

export default CharacterSelection;
